// Third party libs/components
import React, { useState, useEffect, useMemo } from 'react';
import P from 'prop-types';
import { useSelector } from 'react-redux';
import InputMask from 'react-input-mask';
import { useForm, Controller } from 'react-hook-form';
import * as yup from 'yup';
import { parseISO, format } from 'date-fns';

//Components
import CreditCardInput from '@components/CreditCardInput';
import Brands from '../../Brands';

// Style
import * as Styled from '../../../CreditCardForm.styles';

// Utils
import { translate, useCurrentLocale } from '@utils/translate';
import {
  validateCardNumber,
  validateHolderName,
  validateExpiryDate,
  validateHolderDocument,
  validateCVV,
} from '@utils/creditCard';
import { getCurrentLang } from '@utils/language';
import { formatCurrency } from '@utils/currency';
import { MSG_TYPE_CHECK_AVAILABILITY } from '@utils/constants';

const maskProps = {
  maskChar: null,
  maskPlaceholder: null,
  showMask: false,
};

const removeDocumentMasks = texto => texto?.replace(/(\.|\/|\-)/g, '');

const formatCNJPMask = texto =>
  texto?.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/g, '$1.$2.$3/$4-$5');

const validateHolderDocumentEnhanced = value => {
  if (value?.length < 14) {
    return false;
  }
  return validateHolderDocument(value);
};

const textsToTranslate = {
  NEW_CARD: 'newCard',
  NUMBER: 'cardNumber',
  CVV: 'cvv',
  NAME: 'cardHolderName',
  EXPIRATION_DATE: 'validity',
  HOLDER_DOCUMENT: 'docs',
  CANCEL: 'cancel',
  SAVE: 'saveButton',
  WAIT: 'addCardWaitMsg',
  ADDED_CARD: 'addedCard',
  CONTINUE: 'next',
  BACK: 'back',
  COUNTRY: 'country',
  REQUIRED_FIELD: 'requiredField',
  INVALID_CARD_NUMBER: 'invalidCardNumber',
  CONTINUE: 'continue',
  FEEDBACK_TITLE_INTERNATIONAL_CARD:
    'sharedFlightInternationalCardFeedbackTitle',
  FEEDBACK_TITLE_INTERNATIONAL_CARD_PROFILE_SCREEN:
    'sharedFlightInternationalCardFeedbackTitleProfileScreen',
  INTERNATIONAL_PAYMENT: 'internationalPayment',
};

const CreditCardDefaultForm = ({
  onCreate,
  onChange,
  onCancel,
  allowedCountries,
  getProduct,
  hidden,
  countryDocumentInfo,
}) => {
  const language = getCurrentLang();
  const locale = useCurrentLocale();

  const TEXTS = useMemo(() => {
    const translatedText = {};
    for (const text in textsToTranslate) {
      translatedText[text] = translate(textsToTranslate[text]);
    }
    return translatedText;
  }, [language]);

  const schema = useMemo(
    () => ({
      country: {
        name: 'country',
        type: 'text',
        label: TEXTS.COUNTRY,
      },
      number: {
        name: 'number',
        type: 'text',
        required: true,
        type: 'text',
        placeholder: '4111 1111 1111 1111',
        label: TEXTS.NUMBER,
        ...maskProps,
      },
      expiryDate: {
        name: 'expiryDate',
        type: 'text',
        placeholder: 'MM/AA',
        label: TEXTS.EXPIRATION_DATE,
        mask: '99/99',
        ...maskProps,
      },
      cvv: {
        name: 'cvv',
        type: 'text',
        placeholder: 'CVV',
        label: TEXTS.CVV,
        ...maskProps,
      },
      holderName: {
        name: 'holderName',
        type: 'text',
        label: TEXTS.NAME,
        autoFocus: true,
      },
      holderDocument: {
        name: 'holderDocument',
        ...maskProps,
      },
    }),
    [language]
  );

  const validationSchema = useMemo(
    () =>
      yup
        .object()
        .shape({
          country: yup.string().required(TEXTS.REQUIRED_FIELD),
          number: yup
            .string()
            .test('is-valid-card', null, validateCardNumber)
            .required(TEXTS.REQUIRED_FIELD),
          expiryDate: yup
            .string()
            .min(5, null)
            .test('is-valid-date', null, validateExpiryDate)
            .required(TEXTS.REQUIRED_FIELD),
          cvv: yup
            .string()
            .min(3, null)
            .test('is-valid-cvv', null, validateCVV)
            .required(TEXTS.REQUIRED_FIELD),
          holderName: yup
            .string()
            .min(2, null)
            .test('is-valid-name', null, validateHolderName)
            .required(TEXTS.REQUIRED_FIELD),
          holderDocument: yup
            .string()
            .min(14, null)
            .max(18, null)
            .test(
              'is-document-valid',
              TEXTS.INVALID_CARD_NUMBER,
              validateHolderDocumentEnhanced
            )
            .nullable()
            .required(TEXTS.REQUIRED_FIELD),
        })
        .required(),
    [language]
  );

  const { creditCardFormData } = useSelector(state => state.cart);

  const {
    register,
    handleSubmit,
    errors,
    getValues,
    watch,
    control,
    formState,
    setValue,
  } = useForm({
    validationSchema,
    mode: 'onChange',
    shouldUseNativeValidation: false,
    defaultValues: {
      country: creditCardFormData?.country,
      number: '',
      holderName: '',
      expiryDate: '',
      cvv: '',
      holderDocument: '',
    },
  });

  const country = watch(schema.country.name);
  const holderDocument = watch(schema.holderDocument.name);
  const location = window.location.pathname;

  const [isFormValid, setIsFormValid] = useState(false);
  const [docValue, setDocValue] = useState(null);

  useEffect(() => {
    let isAmex =
      creditCardFormData.cardType == 'amex' ||
      creditCardFormData.cardType == 'american_express';

    schema.number.mask = isAmex ? '9999 999999 99999' : '9999 9999 9999 9999';
  }, [creditCardFormData.cardType]);

  useEffect(() => {
    if ((formState.isValid === formState.dirty) === true) {
      setIsFormValid(true);
    } else {
      setIsFormValid(false);
    }
  }, [watch()]);

  useEffect(() => {
    const value = getValues(schema.country.name);
    onChange({
      ...creditCardFormData,
      [schema.country.name]: value,
    });
  }, [watch(schema.country.name)]);

  useEffect(() => {
    if (docValue?.length) {
      setValue(schema.holderDocument.name, docValue, { shouldValidate: true });
    }
  }, [docValue]);

  const onSubmit = data => {
    onChange({
      ...creditCardFormData,
      ...data,
    });
    onCreate();
  };

  const handleOpenChat = () => {
    let textContent = '';
    const productData = getProduct();

    productData.cart.products.forEach(product => {
      const currency = productData.cart.currency;

      const originCity = product.data.flight.originSpot.address.city.name;

      const destinationCity =
        product.data.flight.destinationSpot.address.city.name;

      const seats = product.data.seats;
      const departureDatetime = format(
        parseISO(product.data.flight.departureDatetime),
        "EEEE', 'PPPp",
        {
          locale: locale,
        }
      );
      const aircraft = `${product.data.flight.aircraft.model.manufacturer} ${product.data.flight.aircraft.model.model}`;
      const address = productData.invoice.selectedInvoicingAddress.line;
      const passengers = productData.passengers.reduce((acc, passenger) => {
        const passengerStrInfo = `• ${passenger.firstName} ${passenger.lastName} / ${passenger.identityDocument}\n`;
        return passengerStrInfo + acc;
      }, '');

      const translatedTexts = {
        pt_BR: `\nVoo compartilhado: ${originCity} • ${destinationCity}\nData: ${departureDatetime}\nAeronave: ${aircraft}\nMétodo de pagamento: Cartão de crédito Internacional\nEndereço de faturamento: ${address}\nNúmero de assentos: ${seats}\n\nNomes dos passageiros / Documento:\n${passengers}`,

        en_US: `\nShared Flight: ${originCity} • ${destinationCity}\nDate: ${departureDatetime}\nAircraft: ${aircraft}\nPayment method: International Credit Card\nBilling Address: ${address}\nNumber of seats: ${seats}\n\nPassengers Name / Document:\n${passengers}`,

        es_ES: `\nVuelo compartido: ${originCity} • ${destinationCity}\nFecha: ${departureDatetime}\nAeronave: ${aircraft}\nMétodo de pago: Tarjeta de crédito Internacional\nDirección de facturación: ${address}\nNúmero de asientos: ${seats}\n\nNombre de los pasajeros / Documento:\n${passengers}`,
      };

      textContent += translatedTexts[language];
    });

    const strInfo = {
      pt_BR: `Olá, sou titular de um cartão de crédito internacional e estou interessado em comprar o voo a seguir: ${textContent}`,

      en_US: `Hello, I'm an international credit card holder and I'm interested in buying the flight below: ${textContent}`,

      es_ES: `Hola, soy titular de una tarjeta de crédito internacional y estoy interesado en comprar el vuelo a continuación: ${textContent}`,
    };

    postMessage(
      {
        show: true,
        type: MSG_TYPE_CHECK_AVAILABILITY,
        strInfo: strInfo[language],
      },
      window.location.origin
    );
  };

  const adjustHolderDocumentData = event => {
    let newDocValue = event[0]?.target?.value;
    if (newDocValue?.length > 14 && !newDocValue.includes('/')) {
      setDocValue(formatCNJPMask(removeDocumentMasks(event[0]?.target?.value)));
      return;
    }
    setDocValue(event[0]?.target?.value);
  };

  return (
    <>
      <Styled.HeaderContainer>
        <Styled.Title>
          {country === 'CE' ? TEXTS.INTERNATIONAL_PAYMENT : TEXTS.NEW_CARD}
        </Styled.Title>
        <Brands />
      </Styled.HeaderContainer>
      <Styled.Form show={!hidden} onSubmit={handleSubmit(onSubmit)} noValidate>
        <Styled.InputContainer hidden={country === 'CE'}>
          <Styled.InputColumn>
            <CreditCardInput>
              <select
                defaultValue={creditCardFormData.country || ''}
                ref={register}
                {...schema.country}
                data-test="card.country"
              >
                {allowedCountries &&
                  allowedCountries.map(({ country }) => {
                    return (
                      <option value={country.iso2Code} key={country.iso2Code}>
                        {country.name}
                      </option>
                    );
                  })}
              </select>
              <span>{TEXTS.COUNTRY}</span>
            </CreditCardInput>

            <CreditCardInput>
              <Controller
                as={InputMask}
                control={control}
                defaultValue={creditCardFormData.number || ''}
                ref={register}
                data-test="card.number"
                {...schema.number}
              />
              <span>{TEXTS.NUMBER}</span>
            </CreditCardInput>

            <Styled.InputRow>
              <CreditCardInput>
                <Controller
                  as={InputMask}
                  control={control}
                  data-test="card.expiryDate"
                  defaultValue={creditCardFormData.expiryDate || ''}
                  {...schema.expiryDate}
                />
                <span>{TEXTS.EXPIRATION_DATE}</span>
              </CreditCardInput>
              <CreditCardInput>
                <Controller
                  as={InputMask}
                  control={control}
                  data-test="card.cvv"
                  defaultValue={creditCardFormData.cvv || ''}
                  {...schema.cvv}
                />
                <span>{TEXTS.CVV}</span>
              </CreditCardInput>
            </Styled.InputRow>
          </Styled.InputColumn>

          <Styled.InputColumn>
            <CreditCardInput>
              <input
                data-test="card.holderName"
                defaultValue={creditCardFormData.holderName || ''}
                ref={register}
                {...schema.holderName}
              />
              <span>{TEXTS.NAME}</span>
            </CreditCardInput>

            <CreditCardInput>
              <Controller
                as={InputMask}
                data-test="card.document"
                control={control}
                defaultValue={creditCardFormData.holderDocument || ''}
                mask={
                  holderDocument?.length > 14
                    ? '99.999.999/9999-99'
                    : countryDocumentInfo?.mask + '9999'
                }
                onChange={adjustHolderDocumentData}
                {...schema.holderDocument}
              />
              <span>
                {country === 'BR'
                  ? TEXTS.HOLDER_DOCUMENT
                  : countryDocumentInfo?.name}
              </span>
              {errors[schema.holderDocument.name]?.message?.length > 0 ?? (
                <Styled.FieldErrorMessage>
                  {errors[schema.holderDocument.name]?.message}
                </Styled.FieldErrorMessage>
              )}
            </CreditCardInput>
          </Styled.InputColumn>
        </Styled.InputContainer>

        {country === 'CE' ? (
          // TODO: Mudar o texto quanto a origin for /p/account
          <Styled.InternationalText>
            {location === '/p/account'
              ? TEXTS.FEEDBACK_TITLE_INTERNATIONAL_CARD_PROFILE_SCREEN
              : TEXTS.FEEDBACK_TITLE_INTERNATIONAL_CARD}
          </Styled.InternationalText>
        ) : null}

        <Styled.BtnContainer>
          {/* TODO: Remover os botões e deixar apenas um visível com um "ok" que irá fechar o popup */}
          {location === '/p/account' && country === 'CE' ? (
            <Styled.Button onClick={onCancel} theme="active" type="button">
              OK
            </Styled.Button>
          ) : (
            <>
              <Styled.Button onClick={onCancel} theme="default" type="button">
                {TEXTS.CANCEL}
              </Styled.Button>
              <Styled.Button
                data-test="card.submit"
                theme={
                  country === 'CE'
                    ? 'active'
                    : isFormValid
                    ? 'active'
                    : 'disabled'
                }
                type="submit"
                onClick={country === 'CE' ? handleOpenChat : null}
              >
                {country === 'CE' ? TEXTS.CONTINUE : TEXTS.SAVE}
              </Styled.Button>
            </>
          )}
        </Styled.BtnContainer>
      </Styled.Form>
    </>
  );
};

CreditCardDefaultForm.propTypes = {
  onCreate: P.func,
  onChange: P.func,
  onCancel: P.func,
  getProduct: P.func,
  allowedCountries: P.array,
  hidden: P.bool,
  countryDocumentInfo: P.object,
};

export default CreditCardDefaultForm;
